import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjsBusinessTime from 'dayjs-business-time';
import 'dayjs/locale/fr';
import i18n from '@/settings/i18n';
import { formatStoreDate } from '@/lib/intl-format';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(dayjsBusinessTime);

export const formatDayJsDate = (date: string, storeTimeZone: string) => {
  if (!dayjs(date).isValid()) {
    return '';
  }

  return date?.includes('+')
    ? dayjs(date).tz(storeTimeZone).format('Do MMMM YYYY')
    : dayjs(date).format('Do MMMM YYYY');
};

export const formatDayJsDateShort = (date: string, storeTimeZone: string) => {
  if (!dayjs(date).isValid()) {
    return '';
  }

  const locale = i18n.getLocale();
  // English keeps the ordinal ("5th Jan 2024"); French drops it ("5 janv. 2024").
  const format = locale === 'fr' ? 'D MMM YYYY' : 'Do MMM YYYY';
  const day = date?.includes('+') ? dayjs(date).tz(storeTimeZone) : dayjs(date);

  // EU stores: Intl, no ordinal ("14 ott 2026", "14. Okt. 2026").
  return formatStoreDate(day, 'order') ?? day.locale(locale).format(format);
};

/**
 * A shipment's `deliveredBy` for display. It's kept as `formatDayJsDate`'s
 * English "18th January 2023" because the return-window checks parse it back
 * (`getDeliveryDateFromShipment`); the EU stores show it as Intl's order date.
 * Anything else (a range, "-") is shown as it is.
 */
export const formatDeliveredBy = (deliveredBy: string): string => {
  const day = dayjs(deliveredBy, 'Do MMMM YYYY', 'en', true);

  return (day.isValid() && formatStoreDate(day, 'order')) || deliveredBy;
};
